import { buildApiUrl, getApiHeaders } from '../config/api';

export interface DeviceSipProfile {
  id: number;
  name: string;
  description?: string;
}

type CacheKey = string;

const cache: Record<CacheKey, DeviceSipProfile[]> = {};
const pendingRequests: Record<CacheKey, Promise<DeviceSipProfile[]>> = {};

const buildCacheKey = (domainId?: number | string | null): CacheKey => {
  if (domainId === undefined || domainId === null) {
    return 'global';
  }
  return `domain-${domainId}`;
};

export const fetchDeviceSipProfiles = async (domainId?: number | string | null): Promise<DeviceSipProfile[]> => {
  const cacheKey = buildCacheKey(domainId);

  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  if (!pendingRequests[cacheKey]) {
    const params = new URLSearchParams({ limit: '100' }); // API max limit is 100
    if (domainId !== undefined && domainId !== null) {
      params.append('domainId', String(domainId));
    }

    pendingRequests[cacheKey] = fetch(
      buildApiUrl(`/api/telephony/device-sip-profiles?${params}`),
      {
        headers: getApiHeaders(),
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load Device SIP Profiles');
        }

        const result = await response.json();
        const profiles = result.data || [];
        cache[cacheKey] = profiles;
        return profiles;
      })
      .finally(() => {
        delete pendingRequests[cacheKey];
      });
  }

  return pendingRequests[cacheKey];
};

export const setDeviceSipProfilesCache = (profiles: DeviceSipProfile[], domainId?: number | string | null) => {
  const cacheKey = buildCacheKey(domainId);
  cache[cacheKey] = profiles;
};

export const invalidateDeviceSipProfilesCache = (domainId?: number | string | null) => {
  const cacheKey = buildCacheKey(domainId);
  delete cache[cacheKey];
};
